import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, GitBranch, Trash2, MoreVertical, Edit } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

export default function Workflows() {
  const { t } = useTranslation();
  const [workflows, setWorkflows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [wfRes, agRes] = await Promise.all([
        api.get("/workflows"), api.get("/agents")
      ]);
      setWorkflows(wfRes.data);
      setAgents(agRes.data);
    } catch (e) {
      toast.error(t("wf.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const deleteWorkflow = async (id) => {
    try {
      await api.delete(`/workflows/${id}`);
      toast.success(t("wf.deleted"));
      fetchData();
    } catch (e) {
      toast.error(t("wf.deleteFailed"));
    }
  };

  const getAgentName = (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    return agent ? agent.name : t("wf.unassigned");
  };

  const statusColors = { draft: "#FFD60A", active: "#00E676", inactive: "#888" };

  return (
    <div data-testid="workflows-page" className="p-8 animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-['Manrope'] font-extrabold text-foreground">Workflows</h1>
          <p className="text-muted-foreground mt-1">{t("wf.subtitle")}</p>
        </div>
        <Button
          data-testid="create-workflow-btn"
          onClick={() => navigate("/workflows/new")}
          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(0,230,118,0.3)] active:scale-95 transition-transform duration-150"
        >
          <Plus className="w-4 h-4 mr-2" /> {t("wf.newWorkflow")}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : workflows.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <GitBranch className="w-16 h-16 text-muted-foreground/30 mb-4" />
          <p className="text-lg font-semibold text-muted-foreground">{t("wf.noWorkflows")}</p>
          <p className="text-sm text-muted-foreground/60 mt-1">{t("wf.noWorkflowsSub")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {workflows.map((wf) => (
            <Card
              key={wf.id}
              data-testid={`workflow-card-${wf.id}`}
              className="bg-white border-gray-200 hover:border-primary/30 transition-colors duration-300 cursor-pointer"
              onClick={() => navigate(`/workflows/${wf.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#FFD60A]/10 flex items-center justify-center">
                      <GitBranch className="w-5 h-5 text-[#FFD60A]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{wf.name}</h3>
                      <p className="text-xs text-muted-foreground">{getAgentName(wf.agent_id)}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <button className="p-1 rounded hover:bg-white/5 text-muted-foreground">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-popover border-border">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/workflows/${wf.id}`); }}>
                        <Edit className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => { e.stopPropagation(); deleteWorkflow(wf.id); }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {wf.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{wf.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <Badge
                    variant="outline"
                    className="text-xs capitalize"
                    style={{ borderColor: statusColors[wf.status], color: statusColors[wf.status] }}
                  >
                    {wf.status}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {(wf.nodes || []).length} nodes
                    {(wf.triggers?.rules?.length || 0) > 0 && (
                      <span className="ml-2 text-xs text-primary">
                        · ⚡ {(wf.triggers.rules || []).length} regla{(wf.triggers.rules || []).length > 1 ? "s" : ""} · prioridad {wf.triggers.priority ?? 10}
                      </span>
                    )}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
